import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { AuditRecord } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { CONTROL_STATUS } from '@trustero/trustero-api-web/lib/model/control_pb'
import { CenteredGridColumn } from 'src/components/Reusable/Grid/Grid.styles'
import { useCurrentControl } from 'src/context/FormContext'
import { TitleText } from 'src/components/Reusable/Text/Title'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import {
  ControlsAbsoluteRoutes,
  PoliciesAbsoluteRoutes,
} from 'src/components/Reusable/RootPage/RootPage.constants'
import { GridColumn } from '../../../components/Reusable/Grid'
import { StatusDropdown } from '../../../components/Status'
import { generatePermalinkFromId } from '../../../components/PageLayout/Permalink'
import { useControl } from '../../../components/async/model'
import { DepartmentChip } from '../DepartmentChip'
import { ModelComplianceFrameWorkInfo } from '../../../components/Reusable/Text/ModelComplianceFrameworkInfo'
import { useComplianceFrameworks } from '../../../components/async/complianceframework/useComplianceFrameworks'
import {
  Tooltip,
  TooltipOverlayType,
  TooltipPositions,
} from '../../../components/Reusable/Tooltip/Tooltip'
import { MODEL_TYPES } from '../../../Utils/globalEnums'
import { OwnerAssignmentButton } from './OwnerAssignmentButton'
import { DescriptionMarkup } from './DescriptionMarkup'
import { EvidenceCounts } from './EvidenceCounts'
import { PolicyInfo } from './PolicyInfo'
import { ControlsListItemMenu } from './ControlsListItemMenu/ControlsListItemMenu'
import {
  EvidenceGridColumn,
  PolicyGridColumn,
  RightAlignGridColumn,
  ControlGridRow,
} from './ControlsListItem.styles'
import { ControlModelId } from './ControlsListItem.components'

type ControlsListItemProps = {
  controlModelId: string
  audit?: AuditRecord
}

const NO_POLICY = 'No Policy'
const NO_FRAMEWORK = 'No Framework'
const getComplianceFrameworksForControl = (
  complianceFrameworks: ComplianceFramework[],
  currentIdList: string[],
) =>
  complianceFrameworks.filter((framework: ComplianceFramework) =>
    currentIdList?.includes(framework.getId()),
  ) || []
const getMismatch = (
  complianceFrameworkIdsList: string[],
  complianceFrameworksInUse: ComplianceFramework[],
  hasAudit: boolean,
) => {
  if (!hasAudit || !complianceFrameworkIdsList.length) {
    return false
  }
  return !complianceFrameworksInUse.some((framework) =>
    complianceFrameworkIdsList.includes(framework.getModelId()),
  )
}

export const ControlsListItem = ({
  controlModelId,
  audit,
}: ControlsListItemProps): JSX.Element => {
  const params = useParams()
  const { data, isLoading, mutate } = useControl(controlModelId)
  const { data: cfData, isLoading: isCfLoading } = useComplianceFrameworks(true)
  const { setCurrentControl } = useCurrentControl()

  if (!data || !cfData || isLoading || isCfLoading) {
    return <></>
  }

  const control = data.toObject()
  const complianceFrameworks = cfData.getItemsList() || []
  const {
    id: controlId,
    modelId,
    name,
    departmentId,
    complianceFrameworkIdsList,
    objective,
    policyName,
    status,
    policyId: policyModelId,
  } = control

  const isNotApplicable = status === CONTROL_STATUS.NOTAPPLICABLE

  // TODO: Move all this compliance related logic out of the index list item and into the <ModelComplianceFrameWorkInfo> component
  const complianceFrameworksForControl = getComplianceFrameworksForControl(
    complianceFrameworks,
    complianceFrameworkIdsList,
  )
  const complianceFrameworksInUse = audit
    ? audit.getComplianceFrameworksList()
    : complianceFrameworks
  const mismatch = getMismatch(
    complianceFrameworksForControl.map((el) => el.getModelId()),
    complianceFrameworksInUse,
    !!audit,
  )
  const frameworkNames = complianceFrameworksForControl
    .map((framework) => framework.getName())
    .join(', ')
  const hasComplianceFrameworks = complianceFrameworksForControl.length > 0
  const cfNames = hasComplianceFrameworks ? frameworkNames : NO_FRAMEWORK
  const pName = policyName || NO_POLICY

  const isFromControlsIndex = location.pathname.includes(
    ControlsAbsoluteRoutes.INDEX,
  )
  const isFromPolicyShow = location.pathname.includes(
    PoliciesAbsoluteRoutes.SHOW,
  )
  const includeSearchParams = isFromControlsIndex || isFromPolicyShow
  const search = isFromPolicyShow
    ? {
        [FilterParam.POLICY]: [policyModelId],
      }
    : undefined

  return (
    <ControlGridRow
      id={`controls-index-item-${modelId}`}
      as={Link}
      to={generatePermalinkFromId({
        pageContext: params.pageContext as string,
        modelId,
        isInternalLink: true,
        includeSearchParams,
        search,
      })}
      isNotApplicable={isNotApplicable}
    >
      <GridColumn>
        {!isNotApplicable && <DepartmentChip departmentId={departmentId} />}
      </GridColumn>
      <GridColumn>
        <ControlModelId modelId={modelId} />
      </GridColumn>
      <PolicyGridColumn>
        <Tooltip
          id={`controls-index-row-tooltip-${modelId}`}
          placement={TooltipPositions.right}
          overlayType={TooltipOverlayType.popover}
          tooltipBody={<DescriptionMarkup objective={objective} />}
        >
          <div>
            <PolicyInfo policyName={pName} error={!policyName} />
            <TitleText>{name}</TitleText>
          </div>
        </Tooltip>
      </PolicyGridColumn>
      <RightAlignGridColumn>
        <ModelComplianceFrameWorkInfo
          modelComplianceFrameworkNames={cfNames}
          error={!hasComplianceFrameworks}
          mismatch={mismatch}
          modelType={MODEL_TYPES.CONTROL}
        />
      </RightAlignGridColumn>
      <EvidenceGridColumn>
        {!isNotApplicable && <EvidenceCounts controlId={controlId} />}
      </EvidenceGridColumn>
      <CenteredGridColumn>
        <StatusDropdown
          modelId={modelId}
          modelType={MODEL_TYPE.CONTROL}
          status={control.status}
          isDismissed={control.isDismissed}
          mutate={mutate}
          onToggle={() => setCurrentControl(control)}
        />
      </CenteredGridColumn>
      <CenteredGridColumn>
        <OwnerAssignmentButton control={control} mutate={mutate} />
      </CenteredGridColumn>
      <CenteredGridColumn>
        <ControlsListItemMenu control={control} />
      </CenteredGridColumn>
    </ControlGridRow>
  )
}
